import { useEffect, useState } from 'react'
import { fxProductsApi, fxFuxionCatalogApi } from '../../services/api'
import TableActionButton from '../../components/TableActionButton'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'

interface Product {
  id: number
  name: string
  description: string | null
  unit_cost: number
  sale_price: number
  stock_qty: number
  stock_in_transit_qty: number
  fuxion_catalog_id: number | null
  is_active: boolean
  created_at: string
}

interface FuxionCatalogItem {
  id: number
  product_name: string
  line: string
  presentation: string
  list_price: number
  price_20: number
  price_25: number
  price_30: number
  price_40: number
  price_50: number
  qv: number | null
}

interface ProductForm {
  fuxion_catalog_id: string
  name: string
  description: string
  sale_price: string
}

const emptyForm: ProductForm = {
  fuxion_catalog_id: '', name: '', description: '', sale_price: '',
}

export default function FxProductsPage() {
  const [products, setProducts] = useState<Product[]>([])
  const [catalog, setCatalog] = useState<FuxionCatalogItem[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<ProductForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const fetchAll = async () => {
    try {
      const [productsRes, catalogRes] = await Promise.all([
        fxProductsApi.list(), fxFuxionCatalogApi.list(),
      ])
      setProducts(productsRes.data.products || [])
      setCatalog(catalogRes.data.catalog || [])
    } catch (err) {
      console.error('Error fetching FX products:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const { sortedData, sortConfig, requestSort } = useSortableData(products, {
    key: 'name',
    direction: 'asc',
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleCatalogSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const catalogId = e.target.value
    if (!catalogId) {
      // "Personalizado" -- just unlink, leave whatever the admin already typed as-is.
      setForm((prev) => ({ ...prev, fuxion_catalog_id: '' }))
      return
    }
    const item = catalog.find((c) => String(c.id) === catalogId)
    if (!item) return
    setForm((prev) => ({
      ...prev,
      fuxion_catalog_id: catalogId,
      name: `${item.product_name} (${item.presentation})`,
      sale_price: String(item.list_price),
    }))
  }

  const openNew = () => {
    setForm(emptyForm)
    setEditingId(null)
    setError('')
    setShowForm(true)
  }

  const openEdit = (p: Product) => {
    setForm({
      fuxion_catalog_id: p.fuxion_catalog_id ? String(p.fuxion_catalog_id) : '',
      name: p.name,
      description: p.description || '',
      sale_price: String(p.sale_price),
    })
    setEditingId(p.id)
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('El nombre es requerido')
      return
    }
    const salePrice = parseFloat(form.sale_price)
    if (isNaN(salePrice) || salePrice < 0) {
      setError('Ingresa un precio de venta válido')
      return
    }
    setSaving(true)
    setError('')
    try {
      if (editingId) {
        await fxProductsApi.update(editingId, {
          name: form.name,
          description: form.description || null,
          sale_price: salePrice,
        })
      } else {
        await fxProductsApi.create({
          fuxion_catalog_id: form.fuxion_catalog_id ? Number(form.fuxion_catalog_id) : null,
          name: form.name,
          description: form.description || null,
          sale_price: salePrice,
        })
      }
      setShowForm(false)
      fetchAll()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar producto')
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (p: Product) => {
    try {
      await fxProductsApi.update(p.id, { is_active: !p.is_active })
      fetchAll()
    } catch (err) {
      console.error('Error updating product status:', err)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-espresso-800">Productos — FX Admin</h1>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium"
        >
          + Agregar Producto
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingId ? 'Editar Producto' : 'Nuevo Producto'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}
              {!editingId && (
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Producto del catálogo Fuxion</label>
                  <select
                    name="fuxion_catalog_id" value={form.fuxion_catalog_id} onChange={handleCatalogSelect}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  >
                    <option value="">Personalizado (fuera del catálogo)</option>
                    {catalog.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.product_name} — {c.presentation} (Precio lista: S/ {c.list_price.toFixed(2)})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-espresso-500 mt-1">
                    Al elegir un producto del catálogo se precargan el nombre y el precio de venta (editables).
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Nombre *</label>
                <input
                  type="text" name="name" value={form.name} onChange={handleChange} required
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Descripción</label>
                <textarea
                  name="description" value={form.description} onChange={handleChange} rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Precio de Venta *</label>
                <input
                  type="number" step="0.01" min="0" name="sale_price" value={form.sale_price} onChange={handleChange} required
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              {!editingId && (
                <p className="text-xs text-espresso-500">
                  Todo producto nuevo inicia con stock y costo en cero. Registra una orden de compra en "Órdenes de Compra" para ingresar inventario.
                </p>
              )}
              {editingId && (
                <p className="text-xs text-espresso-500">
                  El stock y el costo se actualizan registrando órdenes de compra, no editando el producto directamente.
                </p>
              )}
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50">
                  {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {products.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay productos aún. Agrega el primero para comenzar.</p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Nombre" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Costo Promedio" sortKey="unit_cost" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Precio Venta" sortKey="sale_price" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Stock" sortKey="stock_qty" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Stock en Tránsito" sortKey="stock_in_transit_qty" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="is_active" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedData.map((p) => (
                  <tr key={p.id} className="hover:bg-cream-100">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-espresso-800">{p.name}</div>
                      {p.description && <div className="text-xs text-espresso-500">{p.description}</div>}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">S/ {p.unit_cost.toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">S/ {p.sale_price.toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                      <span className={p.stock_qty === 0 ? 'text-red-600 font-semibold' : ''}>{p.stock_qty}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                      {p.stock_in_transit_qty > 0 ? p.stock_in_transit_qty : <span className="text-espresso-400">-</span>}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${p.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {p.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1">
                        <TableActionButton onClick={() => openEdit(p)} variant="primary">Editar</TableActionButton>
                        <TableActionButton onClick={() => toggleActive(p)} variant={p.is_active ? 'danger' : 'primary'}>
                          {p.is_active ? 'Desactivar' : 'Activar'}
                        </TableActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
